import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'
import { HelpPanel, HelpButton } from '../ui/HelpPanel'
import { WelcomeTour, useShouldShowTour } from '../ui/WelcomeTour'
import { RegisterNotificationBanner } from './RegisterNotificationBanner'
import { TrialExpiredBanner } from '../TrialExpiredBanner'
import { TrialExpiredModal } from '../TrialExpiredModal'
import { IdleLogout } from '../auth/IdleLogout'
import { setTrialExpiredHandler } from '../../lib/supabase'
import { useAuthStore } from '../../store/authStore'
import { SUBSCRIPTION_LOCKOUT_ENABLED, isTenantLockedOut } from '../../lib/subscriptionLock'
import { SubscriptionLockScreen } from '../subscription/SubscriptionLockScreen'

export function AppLayout() {
  const [helpOpen, setHelpOpen] = useState(false)
  const [showTrialModal, setShowTrialModal] = useState(false)
  const { showTour, dismissTour } = useShouldShowTour()
  const location = useLocation()
  const isPOSRoute = location.pathname.startsWith('/pos')

  useEffect(() => {
    setTrialExpiredHandler(() => setShowTrialModal(true))
    return () => setTrialExpiredHandler(null)
  }, [])

  // Subscription lockout — the single mount point (covers every AppLayout surface:
  // main app, School; POS register /pos/session + /pos/open are full-screen OUTSIDE
  // AppLayout, so they're never covered — an in-progress cart is never at risk).
  //
  // Evaluated ONCE per navigation (mount + pathname change), NOT reactively off
  // authStore.subscription. This is deliberate: useAuth polls the subscription every 60s,
  // and a poll that flips the tenant to "lapsed" while a staff member is filling a form
  // (e.g. /sales/new) must NEVER swap that already-rendered page to the wall and wipe
  // their entry. A user is only ever met by the wall on their NEXT navigation — never
  // yanked mid-task. Fail-open: isTenantLockedOut(null) is false. The wall is a passive
  // screen (it never calls signOut on its own); /subscription is whitelisted so a walled
  // owner can pay out, and after paying the renew flow refreshes authStore.subscription
  // so the next navigation re-evaluates to unlocked.
  const [locked, setLocked] = useState(
    () => SUBSCRIPTION_LOCKOUT_ENABLED && isTenantLockedOut(useAuthStore.getState().subscription),
  )
  useEffect(() => {
    setLocked(SUBSCRIPTION_LOCKOUT_ENABLED && isTenantLockedOut(useAuthStore.getState().subscription))
  }, [location.pathname])

  if (locked && !location.pathname.startsWith('/subscription')) {
    return <SubscriptionLockScreen />
  }

  if (isPOSRoute) {
    return (
      <>
        <TrialExpiredBanner />
        <Outlet />
        <TrialExpiredModal show={showTrialModal} onClose={() => setShowTrialModal(false)} />
        <IdleLogout />
      </>
    )
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <TrialExpiredBanner />
        <RegisterNotificationBanner />
        <TopBar />
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>

      {/* Help system */}
      <HelpButton onClick={() => setHelpOpen(o => !o)} />
      <HelpPanel open={helpOpen} onClose={() => setHelpOpen(false)} />

      {/* First-time welcome tour */}
      {showTour && <WelcomeTour onDone={dismissTour} />}

      <TrialExpiredModal show={showTrialModal} onClose={() => setShowTrialModal(false)} />
      <IdleLogout />
    </div>
  )
}
